import { useEffect, useRef } from 'react'
import type { Order } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { displayOrderNumber } from '../lib/orderNumber'

type Role = 'customer' | 'owner'

function tryBrowserNotification(title: string, body: string, tag: string): void {
  try {
    if (typeof Notification === 'undefined') return
    if (Notification.permission !== 'granted') return
    const n = new Notification(title, { body, tag, icon: '/icon-192.png' })
    setTimeout(() => n.close(), 8000)
  } catch {
    // ignore
  }
}

/**
 * #338 chat unread notifier.
 *
 * Given the orders the current viewer subscribes to plus the viewer's role,
 * this hook does two things:
 *   1. Rewrites `document.title` to `(N) YOKOSO PAY` when there are unread
 *      messages. Restores the original title on unmount.
 *   2. Fires a Browser Notification when the unread count for a *specific*
 *      order increases — skipping the order the viewer is actively reading
 *      (activeOrderId), because then the message is already visible on
 *      screen and a duplicate OS notification would be noise.
 *
 * The counters come from `unreadCountForCustomer` / `unreadCountForOwner`
 * on the Order doc itself, which the API keeps in sync via batch write.
 * We diff them against the previous snapshot so we only notify on *new*
 * activity, not on every re-render.
 */
export function useChatUnreadNotifier(
  orders: Order[],
  role: Role,
  activeOrderId?: string | null,
): void {
  const { t } = useI18n()
  const prevRef = useRef<Map<string, number> | null>(null)
  const originalTitleRef = useRef<string | null>(null)

  const getUnread = (o: Order): number =>
    (role === 'customer' ? o.unreadCountForCustomer : o.unreadCountForOwner) ?? 0

  // Seed / diff on every change. useEffect runs after render so the first
  // pass seeds the map without notifying — "orders I already had unread
  // from before I opened the tab" should NOT re-trigger a notification.
  useEffect(() => {
    if (originalTitleRef.current === null) {
      originalTitleRef.current = document.title
    }

    const totalUnread = orders.reduce((sum, o) => sum + getUnread(o), 0)
    const baseTitle = originalTitleRef.current || 'YOKOSO PAY'
    document.title = totalUnread > 0 ? `(${totalUnread}) ${baseTitle}` : baseTitle

    if (prevRef.current === null) {
      const seed = new Map<string, number>()
      for (const o of orders) seed.set(o.id, getUnread(o))
      prevRef.current = seed
      return
    }

    const prev = prevRef.current
    for (const o of orders) {
      const before = prev.get(o.id) ?? 0
      const now = getUnread(o)
      if (now > before && o.id !== activeOrderId) {
        const orderId = displayOrderNumber(o)
        if (role === 'customer') {
          tryBrowserNotification(
            t('chat.notifyNewFromStoreTitle'),
            t('chat.notifyNewFromStoreBody', { store: '' }),
            `yokoso-chat-${o.id}`,
          )
        } else {
          tryBrowserNotification(
            t('chat.notifyNewFromCustomerTitle'),
            t('chat.notifyNewFromCustomerBody', { id: orderId }),
            `yokoso-chat-${o.id}`,
          )
        }
      }
      prev.set(o.id, now)
    }

    for (const id of prev.keys()) {
      if (!orders.find((o) => o.id === id)) prev.delete(id)
    }
  }, [orders, role, activeOrderId, t])

  // Restore the document title when the hook unmounts (e.g. viewer
  // navigates away from /my-orders). Doing this in a separate effect so it
  // only runs on unmount, not on every re-render.
  useEffect(() => {
    return () => {
      if (originalTitleRef.current !== null) {
        document.title = originalTitleRef.current
      }
    }
  }, [])
}
